// /content/titles/:titleId/versions (E31-S6-T1): a title's versions, for a content owner. The mockups
// have no such screen, so it is drawn as the same hand drew the scripts review: the kit's heading with
// the title's name and tier, then one row per version (version-row.tsx) in the API's order — newest
// first, the pack's edition last. Nothing here sorts or filters.
import type { Principal } from "@waypoint/contracts";
import { cn, focusRing } from "@waypoint/surface";
import { Link } from "wouter";
import { ForbiddenError, NotFoundError } from "../../api/errors.js";
import { t } from "../../i18n/t.js";
import { StatusChip } from "../../ui/form.js";
import { usePlan, useVersionList } from "../../versions/queries.js";
import { EmptyState, Page } from "../page.js";
import { Seg, titleInfoOf } from "./script-format.js";
import { VersionRow } from "./version-row.js";

export function VersionsPage({ titleId, principal }: { titleId: string; principal: Principal }) {
  const list = useVersionList(titleId);
  const plan = usePlan();
  const info = titleInfoOf(plan.data, titleId);

  if (list.isPending)
    return (
      <Page title={info.title}>
        <p role="status" aria-busy className="text-wp-text-3">
          {t("versions.loading")}
        </p>
      </Page>
    );
  if (list.isError) {
    const err = list.error;
    return (
      <Page
        title={t(err instanceof ForbiddenError ? "page.forbidden.title" : "versions.failed.title")}
      >
        <EmptyState
          icon={err instanceof ForbiddenError ? "lock" : "alert"}
          title={
            err instanceof ForbiddenError
              ? t("scripts.forbidden")
              : err instanceof NotFoundError
                ? t("versions.notFound", { id: titleId })
                : t("scripts.failed.line")
          }
        />
      </Page>
    );
  }

  return (
    <section aria-labelledby="page-title" className="grid gap-4 pb-14">
      <p className="m-0 text-13">
        <Link
          href={`/scripts/${encodeURIComponent(titleId)}`}
          className={cn("text-wp-accent underline underline-offset-2", focusRing)}
        >
          {t("versions.back")}
        </Link>
      </p>
      <div className="grid gap-1.5">
        <div className="text-12 font-semibold tracking-[0.16em] text-wp-accent uppercase">
          {t("versions.eyebrow", { id: titleId })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Seg id={titleId} />
          <h1 id="page-title" className="m-0 text-22 leading-tight font-bold">
            {info.title}
          </h1>
          {info.tier === undefined ? null : <StatusChip>{info.tier}</StatusChip>}
        </div>
      </div>
      <section aria-labelledby="versions-heading" className="grid gap-2.5">
        <h2 id="versions-heading" className="m-0 text-14 font-semibold">
          {t("versions.list")}
        </h2>
        <ol className="m-0 grid list-none gap-2 p-0">
          {list.data.versions.map((version) => (
            <VersionRow
              key={version.id ?? `pack-${version.pack.version}`}
              version={version}
              principal={principal}
            />
          ))}
        </ol>
      </section>
    </section>
  );
}
